import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { AuthService } from "src/app/shared/services/authentication/auth.service";
import { PayService } from "src/app/shared/services/pay.service";
import { environment } from "src/environments/environment";

interface Plan {
  name: string;
  price: { monthly: number | string; yearly: number | string };
  priceIds?: {
    monthly: string;
    yearly: string;
  };
  platformFee: string;
  payoutSpeed: string;
  description: string;
  features: string[];
  cta: string;
  disabled?: boolean;
  popular?: boolean;
}

@Component({
  selector: "app-pricing",
  templateUrl: "./pricing.component.html",
  styleUrls: ["./pricing.component.scss"],
})
export class PricingComponent implements OnInit {
  billingCycle: "monthly" | "yearly" = "monthly";
  currentUser: any;
  stripe: Stripe | null = null;
  loadingPlan: string | null = null;
  isLoadingPlans = true;

  plans: Plan[] = [];

  constructor(
    private router: Router,
    private authService: AuthService,
    private payService: PayService
  ) {}

  async ngOnInit() {
    this.stripe = await loadStripe(environment.stripe);

    try {
      this.plans = (await this.payService.getCreatorPlans()) as Plan[];
    } catch (error) {
      console.error("Failed to load pricing plans.", error);
      // Optionally, show an error message to the user in the template
    } finally {
      this.isLoadingPlans = false;
    }

    this.authService.ui$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.updatePlanStates();
      }
    });
  }

  updatePlanStates() {
    if (this.plans.length === 0) return; // Don't run if plans aren't loaded

    // Default to 'creator' if no plan is set on the user object.
    const currentPlanName =
      this.currentUser?.creatorType?.toLowerCase() || "creator";

    const currentPlan = this.plans.find(
      (p) => p.name.toLowerCase() === currentPlanName
    );
    // Use the 'rank' property from the fetched plan data. Default to 0.
    const currentPlanRank = currentPlan ? (currentPlan as any).rank : 0;

    this.plans = this.plans.map((plan) => {
      const planNameLower = plan.name.toLowerCase();
      // Use the 'rank' property from the fetched plan data.
      const planRank = (plan as any).rank;
      const isCurrent = planNameLower === currentPlanName;

      let cta = "";
      let disabled = false;

      if (isCurrent) {
        cta = "Current Plan";
        disabled = true;
      } else if (plan.name === "Label") {
        cta = "Contact Us";
      } else if (planRank < currentPlanRank) {
        cta = "Downgrade";
      } else {
        // This covers planRank > currentPlanRank
        cta = "Upgrade";
      }

      return {
        ...plan,
        cta,
        disabled,
      };
    });
  }

  async selectPlan(plan: Plan) {
    if (!this.currentUser || !this.stripe) {
      console.error("User not logged in or Stripe not loaded.");
      return;
    }

    if (plan.name === "Label") {
      // Handle contact us logic, e.g., open a mailto link or a contact form
      console.log("Contacting sales for Label plan...");
      window.location.href =
        "mailto:sales@apps4artists.uk?subject=Label Plan Inquiry";
      return;
    }

    this.loadingPlan = plan.name;

    const priceId = plan.priceIds?.[this.billingCycle];

    if (!priceId) {
      console.error(
        "Price ID not found for the selected plan and billing cycle.",
        plan
      );
      this.loadingPlan = null;
      return;
    }

    try {
      const result = await this.payService.createCreatorSubscription({
        userId: this.currentUser.id,
        planName: plan.name,
        priceId: priceId,
        billingCycle: this.billingCycle,
      });

      if (result.sessionId) {
        // Path for new subscriptions: redirect to Stripe Checkout
        await this.stripe.redirectToCheckout({ sessionId: result.sessionId });
      } else if (result.success && result.planName) {
        // Path for successful upgrades: redirect to our own success page
        this.router.navigate(["/success"], {
          queryParams: { plan: result.planName, upgrade: "true" },
        });
      } else {
        throw new Error("Failed to create or update the subscription.");
      }
    } catch (error) {
      console.error("Error during plan selection:", error);
    } finally {
      this.loadingPlan = null;
    }
  }

  isNumber(value: any): value is number {
    return typeof value === "number";
  }
}
